import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../api/client";
import { designate } from "../../components/attention";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Motion, Presence } from "../../components/presence";
import { Button } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { authDate, authTime } from "../authText";
import { adminErrorText, eligibilityText, requestAction, targetCodeText } from "./adminText";
import type { AdminRequestRow, Approval } from "./adminTypes";
import { ReauthCancelled, withReauth } from "./reauth";

export const REQUESTS_QUERY = ["unit-requests"] as const;

/** « Demandes à approuver » (docs/17 §5.4, §8.3) : demandes à deux administrateurs en attente. Un administrateur qui
 *  ne peut pas approuver une demande (la sienne, celle qui le vise, nommé depuis moins de 7 jours, code utilisé sur
 *  le poste du dépôt) en lit la raison ; son « Approuver » la désigne. */
export function RequestsPanel({
  onApproved,
  onMessage,
}: {
  onApproved: (approval: Approval) => void;
  onMessage: (message: { tone: "ok" | "danger"; text: string }) => void;
}) {
  const queryClient = useQueryClient();
  const requests = useQuery({ queryKey: REQUESTS_QUERY, queryFn: api.unit.requests, staleTime: 0, refetchInterval: 60_000 });
  const [confirming, setConfirming] = useState<AdminRequestRow | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: REQUESTS_QUERY });
    void queryClient.invalidateQueries({ queryKey: ["unit-accounts"] });
  };
  const approve = useMutation({
    mutationFn: (row: AdminRequestRow) => withReauth(() => api.unit.approve(row.id)),
    onSuccess: (approval) => {
      setConfirming(null);
      refresh();
      onApproved(approval);
    },
    onError: (error) => {
      setConfirming(null);
      if (!(error instanceof ReauthCancelled)) onMessage({ tone: "danger", text: adminErrorText(error) });
      refresh();
    },
  });
  const decline = useMutation({
    mutationFn: (row: AdminRequestRow) => withReauth(() => api.unit.decline(row.id)),
    onSuccess: () => {
      refresh();
      onMessage({ tone: "ok", text: t("Demande retirée") });
    },
    onError: (error) => {
      if (!(error instanceof ReauthCancelled)) onMessage({ tone: "danger", text: adminErrorText(error) });
    },
  });
  const rows = requests.data ?? [];
  return (
    <>
      <Presence show={rows.length > 0}>
        {rows.length > 0 && (
          <Motion origin="top" className="rounded-lg border border-line px-3 py-2">
            <h3 className="text-ui font-semibold text-ink">{t("Demandes à approuver")}</h3>
            <ul className="mt-1">
              {rows.map((row) => {
                const reason = eligibilityText(row.eligibility, row.eligible_from);
                const reasonId = `admin-request-${row.id}`;
                return (
                  <li key={row.id} className="border-b border-line py-2 last:border-b-0">
                    <p className="text-ui leading-snug text-ink">
                      {t("{demandeur} demande : {action} pour {nom} · déposée le {date} · expire à {heure}", {
                        demandeur: row.requested_by.display_name ?? "—",
                        action: requestAction(row.kind),
                        nom: row.target.display_name ?? "—",
                        date: authDate(row.requested_at),
                        heure: authTime(row.expires_at),
                      })}
                    </p>
                    {row.kind === "deactivate" && row.transfer_to && (
                      <p className="mt-0.5 text-meta text-ink-muted">{t("Nouveau propriétaire : {compte}", { compte: row.transfer_to })}</p>
                    )}
                    {targetCodeText(row) && (
                      <p className="mt-0.5 text-meta leading-snug text-warn">{targetCodeText(row)}</p>
                    )}
                    {reason && (
                      <p id={reasonId} data-attention={reasonId} className="mt-0.5 text-meta leading-snug text-ink-muted">
                        {reason}
                      </p>
                    )}
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        blocked={Boolean(row.eligibility)}
                        onBlocked={() => designate(`[data-attention="${reasonId}"]`)}
                        disabled={approve.isPending}
                        onClick={() => setConfirming(row)}
                        aria-describedby={reason ? reasonId : undefined}
                      >
                        {t("Approuver")}
                      </Button>
                      <Button size="sm" variant="secondary" disabled={decline.isPending} onClick={() => decline.mutate(row)}>
                        {row.mine ? t("Annuler la demande") : t("Refuser")}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Motion>
        )}
      </Presence>
      <Presence show={confirming !== null}>
        {confirming && (
          <ConfirmDialog
            title={t("{action} pour {nom}", { action: requestAction(confirming.kind), nom: confirming.target.display_name ?? "—" })}
            confirmLabel={t("Approuver")}
            busyLabel={t("Approbation…")}
            busy={approve.isPending}
            tone={confirming.kind === "reset" || confirming.kind === "promote" ? "neutral" : "danger"}
            onConfirm={() => approve.mutate(confirming)}
            onCancel={() => setConfirming(null)}
          >
            {confirming.kind === "reset"
              ? t("En approuvant, c'est vous qui recevez le code à remettre à {nom}.", { nom: confirming.target.display_name ?? "—" })
              : confirming.kind === "promote"
                ? [t("Approuvez seulement si {nom} existe et a été désigné par l'unité : vous en répondez.", { nom: confirming.target.display_name ?? "—" }), targetCodeText(confirming)]
                    .filter(Boolean)
                    .join(" ")
                : t("Le geste est exécuté dès votre approbation.")}
          </ConfirmDialog>
        )}
      </Presence>
    </>
  );
}
